import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { MenuItem, TextField, Box, TextFieldPropsSizeOverrides, } from '@mui/material';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import { LanguageFlag } from '../ScenarioEditorPage/ScenarioSettings/ScenarioGeneralSettings/LanguageFlag.tsx';
import { languageOptions } from 'app/utils/languageUtils.ts';

export interface LanguageSelectorProps {
  name: string;
  control: any;
  size?: 'small' | 'medium';
  label?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  'data-cy'?: string;
  className?: string;
  onChange?: (value: string) => void;
}

/**
 * A reusable language selector component.
 * Uses the 'as' prop pattern for react-hook-form Controller.
 * Uses i18n translations for language names and supports all languages defined in languageOptions.
 */
export default function LanguageSelector({
  name,
  control,
  size = 'medium',
  label,
  disabled = false,
  fullWidth = true,
  error = false,
  helperText,
  'data-cy': dataCy = 'listOfLanguage',
  className,
  onChange
}: LanguageSelectorProps): JSX.Element {
  const [t] = useTranslation([I18nNamespace.Core]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          margin="normal"
          select
          size={size}
          variant="outlined"
          disabled={disabled}
          label={label}
          fullWidth={fullWidth}
          data-cy={dataCy}
          error={error}
          helperText={helperText}
          className={className}
          value={field.value}
          onChange={(e) => {
            field.onChange(e);
            onChange && onChange(e.target.value);
          }}
          onBlur={field.onBlur}
          name={field.name}
          ref={field.ref}
        >
          {languageOptions.map((lang) => (
            <MenuItem key={lang.value} data-cy={t(lang.labelKey)} value={lang.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageFlag languageCode={lang.flagCode} size="small" />
                {t(lang.labelKey)}
              </Box>
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
}
